import type { ExternalToast, ToasterProps } from 'sonner';
import { useTheme } from 'next-themes';
import React from 'react';
import { toast as sonnerToast, Toaster as Sonner } from 'sonner';
import { cn, Icon } from '@lqa-frontend/ui-system';

interface IToastLinkAction {
	label: React.ReactNode;
	onClick: () => void;
}

interface IToastOptions extends ExternalToast {
	link?: IToastLinkAction;
	isDismissible?: boolean;
}

type ToastVariant = 'success' | 'error' | 'warning' | 'info';

const variantIcons: Record<ToastVariant, React.ReactNode> = {
	success: <Icon name="checkCircleFill" className="text-success" />,
	info: <Icon name="infoCircleFill" className="text-brand-blue-500" />,
	warning: <Icon name="exclamationCircleFill" className="text-orange-500" />,
	error: <Icon name="exclamationTriangleFill" className="text-danger" />,
};

function ToastContent({
	message,
	variant,
	link,
	action,
	isDismissible,
	toastId,
}: {
	message: string;
	variant: ToastVariant;
	link?: IToastLinkAction;
	action?: {
		label: React.ReactNode;
		onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
	};
	isDismissible?: boolean;
	toastId: string | number;
}) {
	return (
		<div className="flex items-center gap-8 w-full whitespace-nowrap">
			{variantIcons[variant]}

			<span className="text-14 font-semibold text-white w-full text-wrap">
				{message}
			</span>

			{link && (
				<button
					type="button"
					onClick={link.onClick}
					className="shrink-0 text-12 text-white underline underline-offset-2 hover:text-white/80 transition-colors"
				>
					{link.label}
				</button>
			)}

			{action && (
				<button
					type="button"
					onClick={action.onClick}
					className="shrink-0 text-12 text-white border border-white rounded-s px-8 py-4 hover:bg-white/10 transition-colors"
				>
					{action.label}
				</button>
			)}

			{isDismissible && (
				<button
					type="button"
					onClick={() => sonnerToast.dismiss(toastId)}
					className="shrink-0 cursor-pointer text-white hover:text-white/70 transition-colors"
					aria-label="Close notification"
				>
					<Icon name="xLg" />
				</button>
			)}
		</div>
	);
}

function createToast(
	variant: ToastVariant,
	message: string,
	options?: IToastOptions
) {
	const {
		link,
		action,
		isDismissible = true,
		...sonnerOptions
	} = options ?? {};

	return sonnerToast.custom(
		(id) => (
			<ToastContent
				message={message}
				variant={variant}
				link={link}
				action={
					action && typeof action === 'object' && 'label' in action
						? (action as {
								label: React.ReactNode;
								onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
							})
						: undefined
				}
				isDismissible={isDismissible}
				toastId={id}
			/>
		),
		{
			duration: 5000,
			...sonnerOptions,
		}
	);
}

const notify = {
	success: (message: string, options?: IToastOptions) =>
		createToast('success', message, options),
	error: (message: string, options?: IToastOptions) =>
		createToast('error', message, options),
	warning: (message: string, options?: IToastOptions) =>
		createToast('warning', message, options),
	info: (message: string, options?: IToastOptions) =>
		createToast('info', message, options),
};

const AlertNotification = ({ ...props }: ToasterProps) => {
	const { theme = 'system' } = useTheme();

	return (
		<Sonner
			theme={theme as ToasterProps['theme']}
			className="toaster group [--gap:8px] md:[--gap:12px]"
			position="bottom-center"
			offset={32}
			gap={12}
			style={{ '--width': '480px' } as React.CSSProperties}
			duration={5000}
			toastOptions={{
				unstyled: true,
				classNames: {
					toast: cn(
						// Center content-sized toasts inside Sonner's fixed-width toaster.
						'left-0 right-0 mx-auto w-fit',
						'bg-brand-blue-950 text-white',
						'rounded-m px-16 py-12',
						'shadow-lg',
						'min-w-240 max-w-320',
						'md:min-w-320 md:max-w-480'
					),
				},
			}}
			{...props}
		/>
	);
};

export { AlertNotification, notify };
export type { IToastOptions, IToastLinkAction };
